import { useTranslation } from 'react-i18next'
interface PainPointsSectionProps {
  title: string
  titleHighlight: string
  body: string
  emphasis: string
}

/**
 * #02 Pain points — blue background + text left + chat bubbles image right.
 * Figma: node 37:12480 (desktop) / 194:6515 (mobile)
 */
export function PainPointsSection({
  title,
  titleHighlight,
  body,
  emphasis,
}: PainPointsSectionProps) {
  const { t: mt } = useTranslation('marketing')

  const [bodyHighlightTail, ...bodyRest] = body.split('\n')
  const bodyWhite = bodyRest.join('\n')
  return (
    <section id="pain-points" data-marketing-section="" className="w-full bg-qb-primary relative overflow-hidden">
      <picture>
        <source media="(max-width: 1023px)" srcSet="/marketing/main/bg-pattern-m.svg" />
        <img
          src="/marketing/main/bg-pattern.svg"
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />
      </picture>

      <div className="relative mx-auto max-w-[1136px] px-6 lg:px-0 py-20 lg:py-[120px]">
        <div data-testid="text-image-split" className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-10">
          {/* Left: text */}
          <div className="flex flex-col gap-10 flex-1 text-white text-center lg:text-left">
            <div className="flex flex-col gap-2">
              <p className="font-['Pretendard',sans-serif] font-bold text-[32px] lg:text-[40px] leading-[1.2] tracking-[-0.6px] whitespace-pre-line">
                <span className="text-[#FFEE00]">
                  {title}
                  {titleHighlight}
                </span>
                <br className="lg:hidden" aria-hidden />
                <span className="lg:hidden">{bodyHighlightTail.trimStart()}</span>
                <span className="hidden lg:inline">{bodyHighlightTail}</span>
                {bodyWhite && '\n' + bodyWhite}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <p className="font-['Pretendard',sans-serif] text-[18px] lg:text-[20px] font-normal leading-[1.5] tracking-[-0.6px] text-white/80 whitespace-pre-line">
                {mt('PainPointsSection.copy361_361')}{'\n'}{mt('PainPointsSection.copy362_362')}
              </p>
              <p className="font-['Pretendard',sans-serif] font-bold text-[22px] lg:text-[28px] leading-[1.4] tracking-[-0.6px]">
                {emphasis}
              </p>
            </div>
          </div>

          {/* Right: chat bubbles (3 floating SVGs) */}
          <div className="flex-1 flex justify-center lg:justify-start">
            <div className="w-full max-w-[560px] flex flex-col gap-2 items-stretch">
              <img
                src="/marketing/main/chat01.svg"
                alt=""
                aria-hidden
                className="w-[78%] h-auto self-start animate-qb-float motion-reduce:animate-none"
                style={{ animationDelay: '0s' }}
              />
              <img
                src="/marketing/main/chat02.svg"
                alt=""
                aria-hidden
                className="w-[82%] h-auto self-end animate-qb-float motion-reduce:animate-none"
                style={{ animationDelay: '0.6s' }}
              />
              <img
                src="/marketing/main/chat03.svg"
                alt=""
                aria-hidden
                className="w-[73%] h-auto self-start animate-qb-float motion-reduce:animate-none"
                style={{ animationDelay: '1.2s' }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
